import { currentToken, getSupportSession, observeCalls } from './api';

// The app's half of the admin journey (the person card in /ops; server side: src/growth/journey.ts). Page views on
// route change, presses of primary buttons and of anything marked data-track, and forms started and submitted.
// What is sent: the event type, a label (data-track, aria-label or the button's own text; a form's data-track,
// aria-label, id or name attribute), the page path with only utm_* query tags. Never a value from a field, never a
// query string. Batched: every 30 seconds, at once on a button press or a submit, and on pagehide as a keepalive
// request (sendBeacon cannot carry the session header, keepalive fetch survives the page the same way). The server
// stamps the person and workspace from the session. Staff pages (/ops) and support sessions are not recorded.

type MoneyType = 'paywall_seen' | 'plan_chosen' | 'checkout_started';
type Ev = { type: 'page_view' | 'cta_click' | 'form_start' | 'form_submit' | MoneyType; name: string | null; page: string; ts: number; data?: Record<string, string | number> };

const URL_PATH = '/portal/track';
const IDLE = 30 * 60_000;
let started = false;
let queue: Ev[] = [];
let flushing = false;
let token: string | null = null;

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => { const r = (Math.random() * 16) | 0; return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16); }));

/** Global Privacy Control or Do Not Track: then the outlevo.com visitor id is never sent along. */
export const optedOut = () => {
  const n = navigator as Navigator & { globalPrivacyControl?: boolean; msDoNotTrack?: string };
  return n.globalPrivacyControl === true || n.doNotTrack === '1' || (window as Window & { doNotTrack?: string }).doNotTrack === '1' || n.msDoNotTrack === '1';
};

/** The first-party visitor id outlevo.com set (the olv_vid cookie on .outlevo.com), unless the browser opted out. */
export function visitorId(): string | null {
  if (optedOut()) return null;
  const m = document.cookie.match(/(?:^|; )olv_vid=([0-9a-f-]{36})(?:;|$)/i);
  return m ? m[1]!.toLowerCase() : null;
}

/** One id per tab, renewed after 30 minutes without activity. */
function sessionId(): string {
  try {
    const [id, at] = (sessionStorage.getItem('olv_sid') ?? '').split('|');
    const sid = id && Date.now() - Number(at) < IDLE ? id : uuid();
    sessionStorage.setItem('olv_sid', `${sid}|${Date.now()}`);
    return sid;
  } catch { return memSid; }
}
const memSid = uuid();

/** A path with only its utm_* tags (and never a fragment). */
export function cleanPath(pathname: string, search = ''): string {
  const q = new URLSearchParams(search);
  const keep = [...q].filter(([k]) => /^utm_[a-z]{1,20}$/i.test(k));
  return (pathname + (keep.length ? `?${new URLSearchParams(keep).toString()}` : '')).slice(0, 300);
}

const skip = () => location.pathname.startsWith('/ops') || Boolean(getSupportSession());
const label = (s: string | null | undefined) => (s ?? '').replace(/\s+/g, ' ').trim().slice(0, 80) || null;

function push(type: Ev['type'], name: string | null, data?: Record<string, string | number>) {
  if (!started || skip()) return;
  queue.push({ type, name, page: cleanPath(location.pathname, location.search), ts: Date.now(), ...(data ? { data } : {}) });
  if (queue.length > 200) queue = queue.slice(-200);
}

/** Sends what is queued (50 at a time). Waits for a session: events before sign-in go out once there is one. */
export async function flush(leaving = false) {
  if (flushing || !queue.length) return;
  if (!leaving) token = await currentToken().catch(() => null);
  // another flush may have taken the queue while the token was read (an empty batch is a 400)
  if (!token || flushing || !queue.length) return;
  flushing = true;
  const batch = queue.splice(0, 50);
  const body = JSON.stringify({ session_id: sessionId(), visitor_id: visitorId(), referrer: document.referrer || null, events: batch });
  try {
    await fetch(URL_PATH, { method: 'POST', keepalive: true, headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` }, body });
  } catch { /* never break the app for analytics */ } finally { flushing = false; }
}

/** The route changed (called from App on every location change). */
export function trackPage() { push('page_view', null); }

/** Primary buttons and elements with data-track: their label only. */
export function ctaName(el: Element): string | null {
  return label(el.getAttribute('data-track')) ?? label(el.getAttribute('aria-label')) ?? label((el as HTMLElement).innerText ?? el.textContent);
}
/** A form's name: data-track, aria-label, id or name attribute (attributes only: never its fields). */
export const formName = (f: Element) => label(f.getAttribute('data-track')) ?? label(f.getAttribute('aria-label')) ?? label(f.getAttribute('id')) ?? label(f.getAttribute('name')) ?? 'form';

/**
 * The money steps (run 13): a wall the person met (which, and the credits short), a plan chosen, a checkout opened.
 * Only labels and numbers; the server adds the workspace's first-touch source and ignores anything else.
 */
export function trackMoney(type: MoneyType, name: string, data?: Record<string, string | number>) {
  push(type, label(name), data);
  void flush();
}

const shortOf = (message: string) => {
  const m = /(\d[\d,]*) credits needed, (\d[\d,]*) available/i.exec(message);
  return m ? Math.max(0, Number(m[1]!.replace(/,/g, '')) - Number(m[2]!.replace(/,/g, ''))) : undefined;
};
const wallOf = (path: string, message: string) =>
  /credits needed/i.test(message) ? 'credits' : /mailbox|sending domain|plan includes/i.test(message) ? 'sending' : /plan allows|upgrade/i.test(message) ? 'plan' : path.split('/')[2] ?? 'other';

/** What the app's own calls say about the money path: refusals for credits or plan, and the billing buttons pressed. */
export function moneyFromCall(c: { method: string; path: string; body?: unknown; error?: { code: string; message: string; status: number } }) {
  const path = c.path.split('?')[0]!;
  const body = (c.body ?? {}) as Record<string, unknown>;
  // the press itself is the choice, whatever the call answers (a refused checkout is a drop-off to see, not nothing)
  const choice = c.method === 'POST' && path === '/portal/billing/checkout' && typeof body.plan === 'string' ? body.plan : null;
  if (choice) trackMoney('plan_chosen', choice, { plan: choice });
  if (c.error) {
    if (c.error.code === 'QUOTA_EXCEEDED' && c.method !== 'GET') {
      const short = shortOf(c.error.message);
      trackMoney('paywall_seen', wallOf(path, c.error.message), { wall: wallOf(path, c.error.message), ...(short !== undefined ? { short } : {}) });
    }
    return;
  }
  if (c.method !== 'POST') return;
  if (choice) trackMoney('checkout_started', choice, { kind: 'plan', plan: choice });
  else if (path === '/portal/billing/topup' && typeof body.packs === 'number') trackMoney('checkout_started', 'topup', { kind: 'topup', packs: body.packs });
  else if (path === '/portal/billing/addons' && body.confirm === true && typeof body.addon_id === 'string') trackMoney('checkout_started', body.addon_id, { kind: 'addon', addon: body.addon_id });
  else if (path === '/portal/billing/ad-wallet/topup' && typeof body.amount_usd === 'number') trackMoney('checkout_started', 'wallet', { kind: 'wallet', amount_usd: body.amount_usd });
}

/** Installs the listeners once (main.tsx). Without this call nothing is recorded (tests, embeds). */
export function startTracking() {
  if (started) return;
  started = true;
  observeCalls(moneyFromCall);
  const forms = new WeakSet<Element>();
  document.addEventListener('click', (e) => {
    const el = (e.target as Element | null)?.closest?.('[data-track], .btn-primary, button[type=submit]');
    if (!el || (el.closest('form') && el.matches('button[type=submit]:not([data-track])'))) return; // a submit is recorded as the form's
    push('cta_click', ctaName(el), { tag: el.tagName.toLowerCase(), ...(el.getAttribute('href') ? { href: cleanPath(...splitHref(el.getAttribute('href')!)) } : {}) });
    void flush();
  }, { capture: true });
  document.addEventListener('focusin', (e) => {
    const f = (e.target as Element | null)?.closest?.('form');
    if (!f || forms.has(f)) return;
    forms.add(f);
    push('form_start', formName(f));
  });
  document.addEventListener('submit', (e) => {
    const f = e.target as Element | null;
    if (!f || f.tagName !== 'FORM') return;
    push('form_submit', formName(f));
    void flush();
  }, { capture: true });
  setInterval(() => { void flush(); }, 30_000);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') void flush(true); });
  window.addEventListener('pagehide', () => { void flush(true); });
  // keep a token at hand for the pagehide batch, which cannot wait for a promise
  void currentToken().then((t) => { token = t; }).catch(() => undefined);
}

const splitHref = (h: string): [string, string] => { try { const u = new URL(h, location.href); return u.origin === location.origin ? [u.pathname, u.search] : [`${u.origin}${u.pathname}`, '']; } catch { return ['', '']; } };

/** tests: forget the queue and start state */
export const _test = { reset() { started = false; queue = []; flushing = false; token = null; observeCalls(null); }, queue: () => queue };
